import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import Navbar from "@components/Navbar";
import { colors } from "../../utils";
import { device } from "../../utils/device";
import { mq } from "../../utils/breakpoints";
import GalleryHeader from "../WheelGallery/Header/GalleryHeader";
import { useSelector } from "react-redux";
import {
  selectPrimaryWheel,
  selectProfile,
} from "../../redux/reducers/gallery";
import { getLatestWheelToShowInReferModal } from "../../services/api";
import { useFeedGateOpen } from "../../utils/feedFirstPaintGate";
import { getLoggedInUser } from "../../utils/helpers";
import { selectAvatarWheelSegments } from "../../utils/selectAvatarWheelSegments";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.white};
`;

const ColumnContainer = styled.div`
  display: flex;
  max-width: 100vw;
  justify-content: space-between;
  overflow: hidden;
  /* Fallback first, then dynamic viewport height. Engines without dvh keep the
     90vh line; modern engines use 90dvh, which avoids the iOS Safari address-bar
     "viewport jump" the plain 90vh causes (cross-browser QA rule / spec §1.2). */
  height: 90vh;
  height: 90dvh;
`;

const LeftColumn = styled.div`
  width: 21%;

  /* Tablet-landscape (900–1199): 21% is only ~215px @1024, which cramps the
     sidebar and wraps its labels. Pin a comfortable fixed width so the % column
     stops driving overflow (spec §1.2). flex-shrink:0 keeps it from squeezing. */
  ${mq.tabletL} {
    width: 240px;
    flex-shrink: 0;
  }

  /* Upper tablet-portrait (769–899, still above the hide cut): slightly narrower
     fixed width to fit the tighter viewport. */
  ${mq.tabletPOnly} {
    width: 220px;
  }

  /* <=768: hidden today (the responsive tab strip in a later unit replaces it).
     Unchanged — U0c must not remove nav. */
  @media ${device.sm} {
    display: none;
  }
`;

const RightColumn = styled.div`
  height: 100%;
  width: 21%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

const CenterColumn = styled.div`
  height: 100%;
  width: 100%;
  margin-top: 5px;
  overflow: scroll;
`;
// NOTE (U0c): §1.2 also proposes a phone gutter (padding: 0 16px) on CenterColumn.
// Measured (u0c-probe): applying it shell-wide shifts content +16px right, which on
// the not-yet-retrofitted dense surfaces (Referrals rdt_Table @840px; Settings dense
// rows) pushes already-overflowing elements further out and INCREASES the offender
// count (fails the strict "0 new offenders" no-regression bar; hScroll stays false).
// The phone gutter is therefore deferred to each surface's own phone retrofit
// (U5 Settings / U13 Referrals), applied AFTER that surface's content is made to fit.

const HeaderRow = styled.div`
  height: 10vh;
`;

const Wrapper = ({
  leftContent,
  centerContent,
  rightContent,
  headerRow,
  style,
  onYugaLogoClick = false,
  isUserProfile = false,
  fullSize = false,
}) => {
  const profile = useSelector(selectProfile);
  const [myWheelToRender, setMyWheelToRender] = useState(null);

  // build a sorted segments array for the LOGGED-IN user's wheel
  const myWheelUpdatedData = useMemo(
    () =>
      myWheelToRender?.wheel_segments?.length > 0
        ? myWheelToRender.wheel_segments.map((obj) => ({ ...obj }))
        : [],
    [myWheelToRender?.wheel_segments]
  );

  const myWheelUpdatedDataSorted = useMemo(
    () =>
      myWheelUpdatedData.length > 0
        ? [...myWheelUpdatedData].sort((a, b) => a.position - b.position)
        : [],
    [myWheelUpdatedData]
  );

  const primaryWheel = useSelector(selectPrimaryWheel);

  // BUG FIX (ISP-9999, "Account's Wheel avatar FRAME is wrongly selected"):
  // the header account avatar previously rendered `myWheelUpdatedDataSorted`
  // unconditionally -- data from the refer-modal "latest wheel" fetch below,
  // never the Primary Wheel. The Primary Wheel wins whenever it has segments;
  // the refer-modal fetch is now only a fallback (first gallery wheel).
  const accountAvatarWheelSegments = useMemo(
    () =>
      selectAvatarWheelSegments(
        primaryWheel?.wheel_segments,
        myWheelUpdatedDataSorted
      ),
    [primaryWheel?.wheel_segments, myWheelUpdatedDataSorted]
  );

  // T22.201: on a cold Discover load, hold the profile-wheel read until the
  // feed's page 1 settles (utils/feedFirstPaintGate.js).
  const feedGateOpen = useFeedGateOpen();
  useEffect(() => {
    const loggedInUserId = getLoggedInUser()?.id;
    if (!loggedInUserId) {
      setMyWheelToRender(null);
      return;
    }
    if (!feedGateOpen) return;

    const fetchMyLatest = async () => {
      const response = await getLatestWheelToShowInReferModal(loggedInUserId);
      if (response?.data?.data?.id) {
        setMyWheelToRender(response.data.data);
        return;
      }
      setMyWheelToRender(null);
    };

    fetchMyLatest();
    // re-run if primary wheel changes (e.g., user updates it)
  }, [primaryWheel, feedGateOpen]);

  // gordon-0925-P1-analytics-page-gray-empty-space-at-bottom-on-scroll:
  // CenterColumn (`main`, overflow:scroll below) is documented as THE one
  // real scroll surface for every page this shell renders (AnchorNav.jsx's
  // own comment, resetSettingsScroll.js's for Settings) -- but html/body
  // were never actually locked out of scrolling too. MEASURED live on
  // web-dev.iyuga.net /referrals?tab=analytics (1920x1080, 1440x900, 390x844,
  // real Chromium wheel gestures): a fast, sustained scroll exhausts
  // CenterColumn's own scroll range and the remaining wheel delta chains onto
  // the window -- `document.documentElement.scrollHeight` measured 2055
  // against a correct `document.body.scrollHeight` of 1028, and
  // `window.scrollY` climbed to 975 while `main.scrollTop` froze wherever the
  // gesture had left it. The shell then visibly slides up out of the
  // viewport, exposing plain body background below it -- the reported gray
  // dead-zone, with the Trends section's content cut off mid-way. Confirmed
  // live: pinning html/body to `overflow: hidden` for as long as this shell
  // is mounted keeps `window.scrollY` at 0 through the same gesture at every
  // width. Same mount/unmount restore pattern ResponsiveDialog already uses
  // for its own scroll lock.
  useEffect(() => {
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.body.style.overflow = prevBodyOverflow;
    };
  }, []);

  return (
    <Container>
      <GalleryHeader
        isUserProfile={isUserProfile}
        wheelUpdatedDataSorted={accountAvatarWheelSegments}
      />
      <ColumnContainer style={style}>
        {!fullSize && <LeftColumn>{leftContent}</LeftColumn>}
        {/* The shared shell's centre column IS the page's main landmark.
            Measured 2026-07-28 on web-stg: /settings had `main,[role=main]` = 0,
            so Settings / Teams / Referrals / Creator Analytics / Notifications /
            Invite / Onboarding shipped with no landmark structure at all.
            `as="main"` keeps every styled-component rule byte-identical (both
            div and main are display:block).

            Deliberately NO id: PR #2328 owns the skip-link target id
            (`MAIN_CONTENT_ID` on a plain wrapper in App.js). Two elements
            sharing that id would be invalid markup. Once #2328 lands, a
            follow-up can point its target at this landmark. */}
        <CenterColumn as="main">{centerContent}</CenterColumn>
        {!fullSize && rightContent && <RightColumn>{rightContent}</RightColumn>}
      </ColumnContainer>
    </Container>
  );
};

export default Wrapper;
